import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { cn } from '@helpers/cn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAPI } from '@providers/useAPI';
import { aiChatKeys } from 'app/queries/aiChat/aiChatKeys';
import { createFetchChatHistoryQueryFn } from 'app/queries/aiChat/createFetchChatHistoryQueryFn';
import toast from 'react-hot-toast';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import CloseIcon from '@components/icons/Close.svg';
import SendIcon from '@components/icons/SendIcon.svg';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { AdminService, PrismaJobTranscriptMessagesHistoryDto } from 'app/API';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Typing } from '../Typing';

type TMessage = Pick<PrismaJobTranscriptMessagesHistoryDto, 'role' | 'content' | 'createdAt'>;

const getTimeFromDate = (date: string) => {
  const d = new Date(date);
  const min = d.getMinutes() > 9 ? d.getMinutes() : `0${d.getMinutes()}`;
  const hour = d.getHours() > 9 ? d.getHours() : `0${d.getHours()}`;
  const sec = d.getSeconds() > 9 ? d.getSeconds() : `0${d.getSeconds()}`;
  return `${hour}:${min}:${sec}`;
};

type TProps = {
  transcriptId: string;
  onCloseClick: () => void;
  repositoryId?: string;
};

export const AIChat = ({ transcriptId, onCloseClick, repositoryId }: TProps) => {
  const [prompt, setPrompt] = useState('');
  const { call } = useAPI();
  const queryClient = useQueryClient();

  const [isRepositoryQuestion, setIsRepositoryQuestion] = useState(false);

  const lastMessageRef = useRef<HTMLDivElement | null>(null);

  const kind = isRepositoryQuestion && repositoryId ? 'repository' : 'transcript';
  const id = kind === 'repository' ? repositoryId! : transcriptId;
  const historyQuery = useQuery<TMessage[], Error, TMessage[], ReturnType<typeof aiChatKeys.detail>>({
    queryKey: aiChatKeys.detail(kind, id),
    queryFn: createFetchChatHistoryQueryFn(call),
    enabled: Boolean(id),
  });
  const sendMutation = useMutation({
    mutationFn: ({
      text,
      kind: targetKind,
      id: targetId,
    }: {
      text: string;
      kind: 'repository' | 'transcript';
      id: string;
      createdAt: string;
    }) => {
      if (targetKind === 'repository') {
        return call(() =>
          AdminService.sendRepositoryQuestionToAi({ requestBody: { userQuestion: text }, repositoryId: targetId }),
        );
      }
      return call(() =>
        AdminService.sendTranscriptQuestionToAi({ requestBody: { userQuestion: text }, transcriptId: targetId }),
      );
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData<TMessage[]>(aiChatKeys.detail(variables.kind, variables.id), (messages = []) => [
        ...messages,
        { role: 'user', content: variables.text, createdAt: variables.createdAt },
        { role: 'system', content: response.answer, createdAt: new Date().toISOString() },
      ]);
    },
    onError: (error, variables) => {
      if (variables.id === id && variables.kind === kind) {
        setPrompt(variables.text);
      }
      toast.error(getApiErrorMessage(error) || 'Unable to send the message');
    },
    onSettled: (_data, _error, variables) =>
      queryClient.invalidateQueries({ queryKey: aiChatKeys.detail(variables.kind, variables.id) }),
  });
  const isWaitingResult = sendMutation.isPending;
  const messagesHistory = useMemo<TMessage[]>(() => {
    const messages = historyQuery.data ?? [];
    const pending = sendMutation.variables;
    return isWaitingResult &&
      pending?.id === id &&
      pending.kind === kind &&
      !messages.some((message) => message.role === 'user' && message.createdAt === pending.createdAt)
      ? [...messages, { role: 'user', content: pending.text, createdAt: pending.createdAt }]
      : messages;
  }, [historyQuery.data, id, isWaitingResult, kind, sendMutation.variables]);
  const sendPrompt = () => {
    if (!prompt.trim() || isWaitingResult) {
      return;
    }
    sendMutation.mutate({ text: prompt, id, kind, createdAt: new Date().toISOString() });
    setPrompt('');
  };

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messagesHistory]);

  return (
    <div className="fixed bottom-4 right-4 w-[500px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100dvh-2rem)] bg-[var(--color-surface)] rounded-[10px] p-5 shadow-lg border border-border flex justify-between flex-col gap-4 z-[9999]">
      <div className="font-semibold text-lg flex justify-between">
        <div className="flex text-[var(--color-title)]">
          AI Chat
          {!!repositoryId && (
            <div className="w-24 ml-4 text-sm">
              <Select
                name="context"
                disabled={isWaitingResult}
                label="Question context"
                value={isRepositoryQuestion ? 'case' : 'job'}
                onChange={(_label, value) => setIsRepositoryQuestion(value === 'case')}
                options={[
                  {
                    value: 'job',
                    label: 'Job',
                  },
                  {
                    value: 'case',
                    label: 'Case',
                  },
                ]}
              />
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label="Close AI chat"
          className="min-h-11 min-w-11 flex items-center justify-center"
          onClick={onCloseClick}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
        {historyQuery.isLoading && <p role="status">Loading chat history...</p>}
        {historyQuery.isError && <p role="alert">Unable to load chat history.</p>}
        {messagesHistory.map((messageInfo, index) => {
          const time = getTimeFromDate(messageInfo.createdAt);
          const currentTime = getAmericanTime(messageInfo.createdAt, false);
          const previousTime = index > 0 ? getAmericanTime(messagesHistory[index - 1].createdAt, false) : null;

          const shouldRenderTimestamp =
            messageInfo.createdAt && messageInfo.role === 'user' && currentTime !== previousTime;

          return (
            <>
              {shouldRenderTimestamp && (
                <div
                  className="self-center text-[var(--color-text-muted)] text-[12px]"
                  key={`ts-${index}-${messageInfo.createdAt}`}
                >
                  {getAmericanTime(messageInfo.createdAt, false)}
                </div>
              )}
              <div
                key={`msg-${index}-${messageInfo.createdAt}`}
                className={cn(
                  'w-[350px] max-w-full rounded-[10px] gap-[10px] pt-[10px] pr-[15px] pb-[10px] pl-[15px] border border-border',
                  messageInfo.role === 'user' && 'bg-[var(--color-surface-disabled)] self-end',
                )}
                ref={index === messagesHistory.length - 1 ? lastMessageRef : null}
              >
                {messageInfo.content.replace(/\[#\d+\]/g, '')}
                {time && <div className="text-[8px] text-end text-[var(--color-text-muted)]">{time}</div>}
              </div>
            </>
          );
        })}
        {isWaitingResult && (
          <div className="text-sm w-full h-[1%] flex mb-4">
            <Typing className="bg-[var(--color-surface-muted)]" />
          </div>
        )}
      </div>

      <Input
        label="Ask anything"
        disabled={isWaitingResult}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setPrompt(event.target.value);
        }}
        type="text"
        value={prompt}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            sendPrompt();
          }
        }}
        icon={SendIcon}
        onIconClick={() => sendPrompt()}
      />
    </div>
  );
};
